import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { X, Check, Bell, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AlertsSystem = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [showAlert, setShowAlert] = useState(null);
  const audioRef = useRef(null);
  const [hasPlayedSound, setHasPlayedSound] = useState(new Set());

  useEffect(() => {
    if (!user) return;

    // Cargar alertas cada 30 segundos
    loadAlerts();
    const interval = setInterval(loadAlerts, 30000);

    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    // Mostrar pop-up y sonido cuando hay nuevas alertas
    if (alerts.length > 0 && !showAlert) {
      const firstAlert = alerts[0];
      
      // Solo mostrar y sonar si no se ha reproducido antes
      if (!hasPlayedSound.has(firstAlert.alert_id)) {
        setShowAlert(firstAlert);
        
        // Reproducir sonido
        if (firstAlert.sound_enabled && audioRef.current) {
          audioRef.current.play().catch(err => console.log('Audio play failed:', err));
        }
        
        // Marcar como reproducido
        setHasPlayedSound(prev => new Set([...prev, firstAlert.alert_id]));
      }
    }
  }, [alerts, showAlert, hasPlayedSound]);

  const loadAlerts = async () => {
    try {
      const res = await axios.get(`${API}/alerts`);
      setAlerts(res.data);
    } catch (error) {
      console.error('Error loading alerts:', error);
    }
  };

  const handleComplete = async (alertId) => {
    try {
      await axios.put(`${API}/alerts/${alertId}/complete`, {});
      toast.success('Alerta completada');
      setShowAlert(null);
      loadAlerts();
    } catch (error) {
      toast.error('Error al completar alerta');
    }
  };

  const handleSnooze = async (alertId) => {
    try {
      await axios.put(`${API}/alerts/${alertId}/snooze`, {});
      toast.info('Alerta pospuesta 24 horas');
      setShowAlert(null);
      loadAlerts();
    } catch (error) {
      toast.error('Error al posponer alerta');
    }
  };

  if (!showAlert) {
    // Badge de contador en la esquina
    return alerts.length > 0 ? (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setShowAlert(alerts[0])}
          className="relative bg-red-500 text-white p-4 rounded-full shadow-lg hover:bg-red-600 transition animate-pulse"
        >
          <Bell size={24} />
          <span className="absolute -top-1 -right-1 bg-yellow-400 text-black text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
            {alerts.length}
          </span>
        </button>
        {/* Audio element for notification sound */}
        <audio ref={audioRef} src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLYiTcIEV6y6OyrWRQKQp3e8LppHgU2jdXzzIExCRdkt+rtpFIZCU2k4/G4bSAGPJDY88NxJgUuhM/z1YU2CRZiturqpFQXB0ed3PLEcSYEL4XQ88l8MAkTYbLo7KpXFAlDnt7yvmwhBSuBzvLYiTcIEV6y6OyrWRQKQp3e8LppHgU2jdXzzIExCRdkt+rtpFIZCU2k4/G4bSAGPJDY88NxJgUuhM/z1YU2CRZiturqpFQXB0ed3PLEcSYEL4XQ88l8MAkTYbLo7KpXFAlDnt7yvmwhBSuBzvLYiTcIEV6y6OyrWRQKQp3e8LppHgU2jdXzzIExCRdkt+rtpFIZCU2k4/G4bSAGPJDY88NxJgUuhM/z1YU2CRZiturqpFQXB0ed3PLEcSYEL4XQ88l8MAkTYbLo7KpXFAlDnt7yvmwhBSuBzvLYiTcIEV6y6OyrWRQKQp3e8LppHgU2jdXzzIExCRdkt+rtpFIZCU2k4/G4bSAGPJDY88NxJgUuhM/z1YU2CRZiturqpFQXB0ed3PLEcSYEL4XQ88l8MAkTYbLo7KpXFAlDnt7yvmwhBSuBzvLYiTcIEV6y6OyrWRQKQp3e8LppHgU2jdXzzIExCRdkt+rtpFIZCU2k4/G4bSAGPJDY88NxJgUuhM/z1YU2CRZiturqpFQXB0ed3PLEcSYEL4XQ88l8MAkTYbLo7KpXFAlDnt7yvmwhBSuBzvLYiTcIEV6y6OyrWRQKQp3e8LppHgU2jdXzzIExCRdkt+rtpFIZCU2k4/G4bSAGPJDY88NxJgUuhM/z1YU2CRZiturqpFQXB0ed3PLEcSYEL4XQ88l8MAkTYbLo7KpXFAlDnt7yvmwhBQ==" />
      </div>
    ) : null;
  }

  // Pop-up de alerta
  return (
    <>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gray-900 border-2 border-red-500 rounded-2xl max-w-md w-full p-6 shadow-2xl animate-bounce">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 bg-red-500/20 rounded-full">
              <AlertCircle size={32} className="text-red-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-white mb-2">¡Atención! Tarea Pendiente</h3>
              <p className="text-gray-300 text-sm mb-2">{showAlert.message}</p>
              <p className="text-gray-500 text-xs">
                Vencimiento: {new Date(showAlert.due_date).toLocaleString('es-ES')}
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => handleComplete(showAlert.alert_id)}
              className="flex-1 flex items-center justify-center gap-2 bg-green-500 text-white rounded-xl py-3 hover:bg-green-600 transition font-semibold"
            >
              <Check size={20} />
              Completar Tarea
            </button>
            <button
              onClick={() => handleSnooze(showAlert.alert_id)}
              className="flex items-center justify-center gap-2 bg-red-500/20 text-red-400 rounded-xl px-4 hover:bg-red-500/30 transition"
            >
              <X size={20} />
            </button>
          </div>

          <p className="text-gray-600 text-xs text-center mt-4">
            ✅ Click verde = Completar | ❌ Click rojo = Posponer 24h
          </p>
        </div>
      </div>

      {/* Audio element */}
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLYiTcIEV6y6OyrWRQKQp3e8LppHgU2jdXzzIExCRdkt+rtpFIZCU2k4/G4bSAGPJDY88NxJgUuhM/z1YU2CRZiturqpFQXB0ed3PLEcSYEL4XQ88l8MAkTYbLo7KpXFAlDnt7yvmwhBSuBzvLYiTcIEV6y6OyrWRQKQp3e8LppHgU2jdXzzIExCRdkt+rtpFIZCU2k4/G4bSAGPJDY88NxJgUuhM/z1YU2CRZiturqpFQXB0ed3PLEcSYEL4XQ88l8MAkTYbLo7KpXFAlDnt7yvmwhBSuBzvLYiTcIEV6y6OyrWRQKQp3e8LppHgU2jdXzzIExCRdkt+rtpFIZCU2k4/G4bSAGPJDY88NxJgUuhM/z1YU2CRZiturqpFQXB0ed3PLEcSYEL4XQ88l8MAkTYbLo7KpXFAlDnt7yvmwhBSuBzvLYiTcIEV6y6OyrWRQKQp3e8LppHgU2jdXzzIExCRdkt+rtpFIZCU2k4/G4bSAGPJDY88NxJgUuhM/z1YU2CRZiturqpFQXB0ed3PLEcSYEL4XQ88l8MAkTYbLo7KpXFAlDnt7yvmwhBSuBzvLYiTcIEV6y6OyrWRQKQp3e8LppHgU2jdXzzIExCRdkt+rtpFIZCU2k4/G4bSAGPJDY88NxJgUuhM/z1YU2CRZiturqpFQXB0ed3PLEcSYEL4XQ88l8MAkTYbLo7KpXFAlDnt7yvmwhBSuBzvLYiTcIEV6y6OyrWRQKQp3e8LppHgU2jdXzzIExCRdkt+rtpFIZCU2k4/G4bSAGPJDY88NxJgUuhM/z1YU2CRZiturqpFQXB0ed3PLEcSYEL4XQ88l8MAkTYbLo7KpXFAlDnt7yvmwhBSuBzvLYiTcIEV6y6OyrWRQKQp3e8LppHgU2jdXzzIExCRdkt+rtpFIZCU2k4/G4bSAGPJDY88NxJgUuhM/z1YU2CRZiturqpFQXB0ed3PLEcSYEL4XQ88l8MAkTYbLo7KpXFAlDnt7yvmwhBQ==" />
    </>
  );
};
